import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Table, Button, Input, Popconfirm, DatePicker, Select, Tag, Avatar } from "antd";
import { PlusOutlined, HistoryOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as CrossIcon } from "../../../assets/cross-icon.svg";
import SendIcon from "../../../assets/send.svg.png";
import AttachmentContainer from "../../LabsStockManagement/tableSection/varianceReportTable/AttachmentContainter";
import VarianceHistoryModal from "../../LabsStockManagement/tableSection/varianceReportTable/VarianceHistoryModal";
import CommentsModal from "../../LabsStockManagement/tableSection/varianceReportTable/CommentsModal";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { fetchVarianceStatuses } from "../../VarianceStatuses/redux/actions";
import { fetchAllVarianceReports, updateVarianceReport, addVarianceComment, fetchVarianceHistory } from "../../LabsStockManagement/redux/actions";

const canEditLab = (labId, userLabs) => {
  if (!labId || !userLabs || userLabs.length === 0) {
    return false;
  }
  const lab = userLabs.find(
    (l) => String(l?.labSetup?.id || l?.id) === String(labId)
  );
  if (!lab) {
    return false;
  }
  const permissions = lab?.permissions || lab?.labsPermissions || {};
  return !!permissions?.general?.add_edit;
};

const DashboardVarianceReportTable = ({ registerId, record }) => {
  const dispatch = useDispatch();
  const [sortedInfo, setSortedInfo] = useState({});
  const [editingKey, setEditingKey] = useState("");
  const [editingField, setEditingField] = useState("");
  const [editValue, setEditValue] = useState("");
  const [localData, setLocalData] = useState([]);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [useServerSorting, setUseServerSorting] = useState(false);

  const isFinalized = !!record?.rawData?.finalizedAt;
  const finalizedTimestamp = record?.rawData?.finalizedAt;

  const varianceReports = useSelector((state) => state.labsStockManagementReducer.varianceReports);
  const fetchingVarianceReportsLoading = useSelector((state) => state.labsStockManagementReducer.fetchingVarianceReportsLoading);
  const allVarianceStatuses = useSelector((state) => state.varianceStatusesReducer.allVarianceStatuses);
  const varianceHistory = useSelector((state) => state.labsStockManagementReducer.varianceHistory);
  const fetchingVarianceHistoryLoading = useSelector((state) => state.labsStockManagementReducer.fetchingVarianceHistoryLoading);
  const userLabs = useSelector((state) => state.authReducer.labs) || [];

  const labId = record?.rawData?.labSetup?.id || record?.rawData?.lab_id;
  const canEdit = canEditLab(labId, userLabs);

  const reportData = registerId ? varianceReports[registerId] || [] : [];

  useEffect(() => {
    if (allVarianceStatuses?.length === 0) {
      dispatch(fetchVarianceStatuses());
    }
  }, [allVarianceStatuses?.length, dispatch]);

  useEffect(() => {
    if (registerId && (!varianceReports[registerId] || varianceReports[registerId].length === 0)) {
      dispatch(fetchAllVarianceReports(registerId));
    }
  }, [registerId, dispatch]);

  const transformVarianceData = (data) => {
    return data.map((item, index) => ({
      key: item.id || `temp-${index}`,
      id: item.id,
      itemCode: item.itemCode || "",
      lotNo: item.lotNo || "",
      expiryDate: item.expiryDate ? dayjs(item.expiryDate).format("YYYY/MM/DD") : "",
      qty: item.qty || 0,
      invNo: item.invNo || "",
      comments: item.comments || [],
      followUpTime: item.followUpTime ? dayjs(item.followUpTime).format("YYYY/MM/DD HH:mm") : "",
      status: item.varianceStatus?.statusName || item.status || "",
      statusColor: item.varianceStatus?.color,
      attachments: item.attachments || [],
      historyCount: item._count?.histories || 0,
    }));
  };

  useEffect(() => {
    if (reportData.length > 0) {
      setLocalData(transformVarianceData(reportData));
    }
  }, [reportData, registerId]);

  // Update localData when Redux state changes during server-side sorting
  useEffect(() => {
    if (useServerSorting && varianceReports[registerId]) {
      const serverData = varianceReports[registerId] || [];
      setLocalData(transformVarianceData(serverData));
    }
  }, [varianceReports, registerId, useServerSorting]);

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    
    const recordCount = reportData.length;
    const useServerSorting = recordCount > 1000;
    
    if (sorter.columnKey && sorter.order && registerId) {
      if (useServerSorting) {
        const sortParams = {
          sortBy: sorter.columnKey,
          sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
        };
        dispatch(fetchAllVarianceReports(registerId, sortParams));
        setUseServerSorting(true); 
      } else {
        const sortedData = [...data].sort((a, b) => {
          const aValue = a[sorter.columnKey];
          const bValue = b[sorter.columnKey];
          
          if (aValue === undefined || aValue === null) return 1;
          if (bValue === undefined || bValue === null) return -1;
          
          if (sorter.order === 'ascend') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
        setLocalData(sortedData);
        setUseServerSorting(false);
      }
    } else if (registerId && !useServerSorting) {
      setLocalData(transformVarianceData(reportData));
    }
  };

  const isEditing = (record, field) => (record.id || record.key) === editingKey && field === editingField;

  const handleEdit = (record, field) => {
    setEditingKey(record.id || record.key);
    setEditingField(field);
    if (field === "comments") {
      setEditValue("");
    } else if (field === "followUpTime") {
      // Ensure we are parsing a valid date string from the record
      const dateValue = record[field];
      setEditValue(dateValue ? dayjs(dateValue) : null);
    } else if (field === "status") {
      setEditValue(record.status || "");
    } else {
      setEditValue(record[field] || "");
    }
  };

  const handleCancel = () => {
    setEditingKey("");
    setEditingField("");
    setEditValue("");
  };

  const handleSave = (record) => {
    if (record && registerId) {
      if (editingField === "comments") {
        if (editValue && editValue.trim() !== "") {
          dispatch(addVarianceComment(registerId, record.id || record.key, { content: editValue }));
        }
      } else {
        const newData = [...localData];
        const index = newData.findIndex((item) => (item.id || item.key) === (record.id || record.key));

        if (index > -1) {
          const item = newData[index];
          let updatedValue = editValue;
          let updateData = { [editingField]: editValue };

          if (editingField === "status") {
            const selectedStatus = allVarianceStatuses.find(s => s.statusName === editValue);
            if (selectedStatus) {
              updateData = { varianceStatusId: selectedStatus.id };
            }
          }

          newData.splice(index, 1, {
            ...item,
            [editingField]: updatedValue,
          });
          setLocalData(newData);
          dispatch(updateVarianceReport(registerId, record.id || record.key, updateData));
        }
      }
      handleCancel();
    }
  };

  const handleHistoryClick = (record) => {
    const reportId = record.id || record.key;
    setSelectedReportId(reportId);
    setHistoryModalVisible(true);
    if (registerId) {
      dispatch(fetchVarianceHistory(registerId, reportId));
    }
  };

  const handleHistoryModalClose = () => {
    setHistoryModalVisible(false);
    setSelectedReportId(null);
  };

  const data = (useServerSorting && localData.length === 0) ?
    (varianceReports[registerId] || []) :
    (localData.length > 0 ? localData : transformVarianceData(reportData));

  const getStatusColor = (status, record) => {
    if (record?.statusColor) return record.statusColor;
    const statusLower = status?.toLowerCase();
    if (statusLower === "completed" || statusLower === "resolved") return "#a7e0a5";
    if (statusLower === "pending" || statusLower === "in progress") return "#fde28d";
    return "#fcb9b8";
  };

  const columns = useMemo(() => [
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Stock Code</div>
        </div>
      ),
      dataIndex: "itemCode",
      key: "itemCode",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "itemCode" && sortedInfo.order,
      width: "11%",
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Lot No</div>
        </div>
      ),
      dataIndex: "lotNo",
      key: "lotNo",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "lotNo" && sortedInfo.order,
      width: "11%",
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Expiry Date</div>
        </div>
      ),
      dataIndex: "expiryDate",
      key: "expiryDate",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "expiryDate" && sortedInfo.order,
      width: "10%",
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Qty</div>
        </div>
      ),
      dataIndex: "qty",
      key: "qty",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "qty" && sortedInfo.order,
      width: "8%",
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Iwt no/ Inv no*</div>
        </div>
      ),
      dataIndex: "invNo",
      key: "invNo",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "invNo" && sortedInfo.order,
      width: "10%",
      render: (text, record) => {
        const editable = isEditing(record, "invNo");
        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ width: "55px !important", height: "23px !important", boxShadow: "none", borderRadius: "2px" }}
              size="small"
              className="stock-input"
            />
            <SaveIcon
              className="custom-icon"
              style={{ cursor: "pointer", width: "15px", height: "15px" }}
              onClick={() => handleSave(record)}
            />
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "18px", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span>{text}</span>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
                width: "15px",
                height: "15px"
              }}
              onClick={() => canEdit && handleEdit(record, "invNo")}
            />
          </div>
        );
      },
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Comments</div>
        </div>
      ),
      dataIndex: "comments",
      key: "comments",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "comments" && sortedInfo.order,
      width: "30%",
      render: (text, record) => {
        const editable = isEditing(record, "comments");
        const commentsList = Array.isArray(record.comments) ? record.comments : [];

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
            {editable ? (
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onPressEnter={() => handleSave(record)}
                  placeholder="Type a Message"
                  className="stock-input"
                  style={{ width: "100%", height: "24px", boxShadow: "none", borderRadius: "2px", borderColor: "#D9D9D9" }}
                  suffix={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <img
                        src={SendIcon}
                        alt="send"
                        style={{ cursor: "pointer", width: "16px", height: "16px" }}
                        onClick={() => handleSave(record)}
                      />
                      <Popconfirm
                        title="Are you sure you want to cancel?"
                        onConfirm={handleCancel}
                        okText="Yes"
                        cancelText="No"
                      >
                        <CrossIcon
                          className="custom-icon"
                          style={{ cursor: "pointer", width: "16px", height: "16px" }}
                        />
                      </Popconfirm>
                    </div>
                  }
                />
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <Button
                  icon={<PlusOutlined />}
                  className="add-doc-btn"
                  size="middle"
                  disabled={!canEdit}
                  onClick={() => handleEdit(record, "comments")}
                >
                  COMMENT
                </Button>
              </div>
            )}

            {commentsList.slice().reverse().slice(0, 2).map((comment, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #D9D9D9",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#fff",
                  marginTop: "4px",
                  width: "100%"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                  <Avatar
                    size={18}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#A5D6A7" : "#BBDEFB",
                      color: "#101820",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {typeof comment.user === 'string' ? comment.user.charAt(0) : (comment.user?.firstName?.charAt(0) || "U")}
                  </Avatar>
                  <span style={{ fontSize: "12px", fontWeight: "500", color: "#101820" }}>
                    {typeof comment.user === 'string' ? comment.user : `${comment.user?.firstName || ""} ${comment.user?.lastName || ""}`.trim() || "Unknown User"}
                  </span>
                  <span style={{ fontSize: "12px", color: "#101820" }}>
                    - {comment.date || (comment.createdAt ? dayjs(comment.createdAt).format("YYYY/MM/DD") : "")} {comment.time || (comment.createdAt ? dayjs(comment.createdAt).format("HH:mm") : "")}
                  </span>
                </div>
                <div style={{ fontSize: "14px", color: "#383838", paddingTop: "8px" }}>
                  {comment.text || comment.content}
                </div>
              </div>
            ))}

            {commentsList.length > 2 && (
              <Button
                type="link"
                onClick={() => {
                  setSelectedComments(commentsList);
                  setCommentsModalVisible(true);
                }}
                style={{
                  padding: "4px 0",
                  marginLeft: "8px",
                  fontSize: "12px",
                  fontFamily: "poppins, Arial",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  color: "#101820",
                  fontWeight: "500",
                  textDecoration: "underline"
                }}
              >
                See All Comments
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Follow Up Time</div>
        </div>
      ),
      dataIndex: "followUpTime",
      key: "followUpTime",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "followUpTime" && sortedInfo.order,
      width: "19%",
      render: (text, record) => {
        const editable = isEditing(record, "followUpTime");
        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <DatePicker
              showTime
              value={editValue}
              onChange={(date) => setEditValue(date)}
              format="YYYY/MM/DD HH:mm"
              style={{ width: "135px", height: "24px", borderRadius: "2px", padding: "0" }}
              size="small"
              allowClear={false}
              suffixIcon={null}
              prefix={null}
              className="follow-up-picker"
            />
            {editValue && (
              <SaveIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "15px", height: "15px" }}
                onClick={() => handleSave(record)}
              />
            )}
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "18px", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : record.followUpTime ? (
          <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "max-content" }}>
            <span>{dayjs(record.followUpTime).format("YYYY/MM/DD HH:mm")}</span>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
                width: "15px",
                height: "15px"
              }}
              onClick={() => canEdit && handleEdit(record, "followUpTime")}
            />
          </div>
        ) : (
          <Button
            icon={<PlusOutlined />}
            className="add-doc-btn"
            size="middle"
            disabled={!canEdit}
            onClick={() => handleEdit(record, "followUpTime")}
          >
            FOLLOW UP
          </Button>
        );
      },
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Status*</div>
        </div>
      ),
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "status" && sortedInfo.order,
      width: "15%",
      render: (text, record) => {
        const editable = isEditing(record, "status");
        const statusName = record.status;
        const statusColor = getStatusColor(statusName, record);

        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Select
              value={editValue || undefined}
              onChange={(value) => setEditValue(value)}
              placeholder="Select Status"
              style={{ width: "70%", height: "24px", borderRadius: "2px" }}
              size="small"
              className="stock-input"
              dropdownStyle={{ minWidth: "140px" }}
            >
              {allVarianceStatuses.map((status) => (
                <Select.Option key={status.id} value={status.statusName}>
                  {status.statusName}
                </Select.Option>
              ))}
            </Select>
            <SaveIcon
              className="custom-icon"
              style={{ cursor: "pointer", width: "20%", height: "15px" }}
              onClick={() => handleSave(record)}
            />
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "20%", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : statusName ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Tag
              color={statusColor}
              style={{
                color: "#383838",
                width: "100px",
                height: "20px",
                borderRadius: "10px",
                textAlign: "center",
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: 'none',
              }}
            >
              {statusName}
            </Tag>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
                width: "15px",
                height: "15px"
              }}
              onClick={() => canEdit && handleEdit(record, "status")}
            />
          </div>
        ) : (
          <Button
            icon={<PlusOutlined />}
            className="add-doc-btn"
            size="middle"
            disabled={!canEdit}
            onClick={() => handleEdit(record, "status")}
          >
            STATUS
          </Button>
        );
      },
    },
    {
      title: () => (
        <div align="left" className="select-parent-div">
          <div className="stock-table-header-text">Attach</div>
        </div>
      ),
      dataIndex: "attachments",
      key: "attachments",
      width: "9%",
      render: (attachments, record) => (
        <AttachmentContainer
          recordType="variance_report"
          record={record}
          color="#ed017f"
          disabled={!canEdit}
          isExpanded={false}
        />
      ),
    },
    {
      title: () => (
        <div align="center" className="select-parent-div">
          <div className="stock-table-header-text"></div>
        </div>
      ),
      dataIndex: "history",
      key: "history",
      width: "8%",
      render: (text, record) => {
        const hasHistory = record.historyCount > 0;

        return hasHistory ? (
          <HistoryOutlined
            style={{ cursor: "pointer", fontSize: "16px", color: "#ed017f" }}
            onClick={(e) => {
              e.stopPropagation();
              handleHistoryClick(record);
            }}
          />
        ) : null;
      },
    },
  ], [sortedInfo, canEdit, allVarianceStatuses, editValue, editingKey, editingField, registerId, varianceHistory, fetchingVarianceHistoryLoading, localData]);

  return (
    <Row className="stock-doc-control-parent-row">
      <Col span={24} className="stock-table-heading-col">
        <Row align="middle" gutter={[50, 0]} justify="space-between">
          <Col>
            <Row gutter={[50, 0]} align="middle">
              <Col>
                <div>
                  <span className="heading-span">Variance Report</span>
                </div>
              </Col>
              {finalizedTimestamp ? (
                <Col>
                  <span className="heading-span">
                    Stock take finalized - {dayjs(finalizedTimestamp).format('DD MMM YYYY HH:mm')}
                  </span>
                </Col>
              ) : (
                <Col>
                  <span className="heading-span" style={{ color: '#ed017f' }}>
                    Stock take not finalized
                  </span>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Table
          loading={fetchingVarianceReportsLoading}
          scroll={{ y: '350px' }}
          className={`special-table stock-table ims-doc-table reduced-padding-table`}
          rowClassName="editable-row"
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey={(record) => record.id || record.key}
          locale={{ emptyText: 'No variance reports generated' }}
          onChange={handleChange}
        />
      </Col>
      <VarianceHistoryModal
        visible={historyModalVisible}
        onClose={handleHistoryModalClose}
        history={selectedReportId ? (varianceHistory[selectedReportId] || []) : []}
        loading={fetchingVarianceHistoryLoading}
        title="History"
      />
      <CommentsModal
        visible={commentsModalVisible}
        onClose={() => setCommentsModalVisible(false)}
        comments={selectedComments}
        loading={false}
        title="Comments"
      />
    </Row>
  );
};

export default DashboardVarianceReportTable;

